import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable, NgZone } from "@angular/core";
import { fromEvent, merge, Observable, Subject, Subscription, takeUntil, throttleTime, timer } from "rxjs";
import { Router } from "@angular/router";
import { NgHttpService } from "./httpservice";
import { environment } from "@/app/environments/environment";
import { LoginRequest } from "@/app/model/auth.model";

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private loginRequest!: LoginRequest;

  private destroy$ = new Subject<void>();
  private inatividadeTempo?: Subscription;
  private storageHandler?: (event: StorageEvent) => void;

  public inativo$ = new Subject<void>();

  private tempoInatividade = 10 * 60 * 1000;
  private usuarioInativo = false;

  constructor(
    private http: HttpClient,
    private httpService: NgHttpService,
    private _router: Router,
    private ngZone: NgZone
  ) {
    this.verificarInatividade();
  }

  private verificarInatividade() {
    const activityEvents = [
      'mousemove',
      'mousedown',
      'keypress',
      'scroll',
      'touchstart'
    ];

    this.ngZone.runOutsideAngular(() => {
      merge(...activityEvents.map(event => fromEvent(document, event)))
        .pipe(
          throttleTime(1000),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          this.ultimaAtividade();
        });
    });

    this.storageHandler = (event: StorageEvent) => {
      if (event.key === 'ultimaAtividade') {
        this.resetarTempoInatividade(false);
      }
    };

    window.addEventListener('storage', this.storageHandler);

    this.resetarTempoInatividade();
  }

  private ultimaAtividade() {
    if (this._router.url.startsWith('/auth')) return;
    if (this.usuarioInativo) return;

    localStorage.setItem('ultimaAtividade', Date.now().toString());
    this.resetarTempoInatividade(false);
  }

  private resetarTempoInatividade(updateStorage: boolean = true) {
    this.inatividadeTempo?.unsubscribe();

    if (updateStorage) {
      localStorage.setItem('ultimaAtividade', Date.now().toString());
    }

    this.inatividadeTempo = timer(this.tempoInatividade)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this._router.url.startsWith('/auth')) return;
        if (this.usuarioInativo) return;

        this.usuarioInativo = true;

        this.ngZone.run(() => {
          this.inativo$.next();
        });
      });
  }

  public reativarUsuario() {
    this.usuarioInativo = false;
    localStorage.setItem('ultimaAtividade', Date.now().toString());
    this.resetarTempoInatividade(false);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    this.inatividadeTempo?.unsubscribe();

    if (this.storageHandler) {
      window.removeEventListener('storage', this.storageHandler);
    }
  }

  public login(user: LoginRequest): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.post(
      `${environment.apiGeral}/api/auth/loginAcessoExterno`, user,
      {
        headers
      }
    );
  }

  confirmar(Token: string): Observable<any> {
    return this.httpService.get(environment.apiGeral, `Usuario/Ativar`, { Token });
  }

  getUserLogged() {
    return JSON.parse(localStorage.getItem('userLogged') || '');
  }

  getUserLoggedAdmin() {
    return JSON.parse(localStorage.getItem('userLoggedAdmin') || '');
  }

  usuarioLogado(params: any = {}): Observable<any> {
    return this.http
      .get(`${environment.apiGeral}/api/Usuario/UsuarioLogado`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`
        }
      })
  }

  getMenu(params: any = {}): Observable<any> {
    return this.http
      .get(`${environment.apiGeral}/api/Clientes/RetornarMenuUsuario`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`
        }
      })
  }

  retornaTipoCliente(params: any): Observable<any> {
    return this.http
      .get(`${environment.apiGeral}/api/Clientes/RetornaTipoCliente`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`
        }
      })
  }

  listarPorPerfilSub(perfilId: any, subFuncionalidadeId: any): Observable<any> {
    return this.http
      .get(`${environment.apiGeral}/api/PerfilControleAcesso/ListarPorPerfilSub?perfilId=${perfilId}&codigoSubFuncionalidade=${subFuncionalidadeId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`
        }
      })
  }

  loggedIn(): boolean {
    const token = localStorage.getItem('access_token');

    if (!token) return false;

    try {
      const payload = JSON.parse(atob(token.split('.')[1])); 
      const exp = payload.exp * 1000;
      return Date.now() < exp;
    } catch (e) {
      return false;
    }
  }

  setLoginRequest(login: LoginRequest) {
    this.loginRequest = login;
  }

  getLoginRequest(): LoginRequest {
    return this.loginRequest;
  }

  codigoLogin(params: any): Observable<any> {
    return this.httpService.post(environment.apiGeral, `Usuario/CodigoLogin`, params);
  }

  confirmarCodigo(params: any): Observable<any> {
    return this.httpService.post(environment.apiGeral, `Usuario/ConfirmarCodigoLogin`, params);
  }

  alterarEmpresa(body: any): Observable<any> {
    return this.httpService.post(environment.apiGeral, `Clientes/AlterarEmpresa`, body)
  }

  listarFiliais(params: any = {}): Observable<any> {
    return this.httpService.get(environment.apiGeral, `Clientes/ListarFiliais`, params)
  }

  logout() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('userLogged');
    this._router.navigate(['auth/login']);
  }
}
